import React, { useMemo, useState } from "react";
import {
  PermitSignatureCard,
  SignatureViewModal,
  getMakerPositionLabel,
  getCompletedSignatures,
  findSignature,
  getSigUrl,
  sameText,
  formatSignatureDateTime,
} from "./PermitSignatureCard";
import { Paperclip, MessageSquare } from "lucide-react";
import { resolveMediaUrl } from "../../../../lib/utils";
import { getPermitMakerDisclaimer } from "./permitHelpers";

const getSignatureGroupCode = (sig, log) => {
  const code =
    sig?.actor_group_code ||
    log?.actor_group_code ||
    log?.from_group_code ||
    "";

  if (code) return code;

  const name = String(
    sig?.actor_group_name ||
    log?.actor_group_name ||
    log?.from_group_name ||
    ""
  ).trim();

  if (name === "Contractor EHS") return "CONTRACTOR_EHS";
  if (name === "Contractor Engineer") return "CONTRACTOR_ENGINEER";
  if (name === "ADL Engineer") return "ADL_ENGINEER";
  if (name === "ADL EHS") return "ADL_EHS";

  return "";
};

const buildIssueSignatureRows = (permit) => {
  const signatures = getCompletedSignatures(permit);

  const rows = [
    {
      key: "issue_maker_signature",
      label: "Permit Applicant / Maker",
      subLabel: getMakerPositionLabel(permit),
      badge: "ISSUED BY",
      signatureObj: findSignature(
        signatures,
        (sig) =>
          sig.signature_box_key === "issue_maker_signature" ||
          sameText(sig.workflow_action, "create") ||
          sameText(sig.workflow_action, "submit")
      ),
    },
    {
      key: "issue_checker_signature1",
      label: "Contractor EHS",
      subLabel: "Contractor EHS",
      badge: "Approved By",
      signatureObj: findSignature(
        signatures,
        (sig) =>
          sig.signature_box_key === "issue_checker_signature1" ||
          (sameText(getSignatureGroupCode(sig, null), "CONTRACTOR_EHS") &&
            !sameText(sig.status_from, "closure_in_progress"))
      ),
    },
    {
      key: "issue_checker_signature2",
      label: "Contractor Engineer",
      subLabel: "Contractor Engineer",
      badge: "Approved By",
      signatureObj: findSignature(
        signatures,
        (sig) =>
          sig.signature_box_key === "issue_checker_signature2" ||
          (sameText(getSignatureGroupCode(sig, null), "CONTRACTOR_ENGINEER") &&
            !sameText(sig.status_from, "closure_in_progress"))
      ),
    },
    {
      key: "issue_checker_signature3",
      label: "ADL Engineer",
      subLabel: "ADL Engineer",
      badge: "Approved By",
      signatureObj: findSignature(
        signatures,
        (sig) =>
          sig.signature_box_key === "issue_checker_signature3" ||
          (sameText(getSignatureGroupCode(sig, null), "ADL_ENGINEER") &&
            !sameText(sig.status_from, "closure_in_progress"))
      ),
    },
    {
      key: "issue_checker_signature4",
      label: "ADL EHS",
      subLabel: "ADL EHS",
      badge: "Approved By",
      signatureObj: findSignature(
        signatures,
        (sig) =>
          sig.signature_box_key === "issue_checker_signature4" ||
          (sameText(getSignatureGroupCode(sig, null), "ADL_EHS") &&
            !sameText(sig.status_from, "closure_in_progress"))
      ),
    },
  ];

  return rows
    .filter((row) => row.signatureObj && getSigUrl(row.signatureObj))
    .map((row) => {
      const sig = row.signatureObj;
      let name =
        sig.performed_by_name ||
        sig.actor_name ||
        sig.actor_group_name ||
        "";

      if (
        (!name || String(name).trim().toLowerCase().startsWith("user ")) &&
        permit?.created_by_name &&
        row.key.includes("maker")
      ) {
        name = permit.created_by_name;
      }

      const designation =
        sig.actor_designation ||
        sig.designation ||
        sig.actor_designation_name ||
        sig.actor_role_name ||
        (row.key.includes("maker") ? permit?.created_by_designation : "") ||
        row.subLabel;

      if (!name) {
        name = row.label;
      }

      const isMaker = row.key.includes("maker");
      const declarationText = isMaker
        ? sig.consent_data?.text || getPermitMakerDisclaimer(permit)
        : sig.consent_data?.text || "";

      return {
        key: row.key,
        label: row.label,
        subLabel: row.subLabel,
        designation: designation,
        badge: row.badge,
        name: name,
        signature: getSigUrl(sig),
        signedAtDisplay: sig.signed_at_display || "",
        signedAt: sig.performed_at || sig.created_at || "",
        action: sig.workflow_action || sig.action || "",
        groupName: sig.actor_group_name || "",
        declarationText: declarationText,
        rawSig: sig,
      };
    });
};

const buildClosureSignatureRows = (permit) => {
  const signatures = getCompletedSignatures(permit);

  const isClosureApprove = (sig) =>
    (sameText(sig.workflow_action || sig.action, "approve") ||
      sameText(sig.workflow_action || sig.action, "close")) &&
    sameText(sig.status_from, "closure_in_progress");

  const rows = [
    {
      key: "closure_maker_signature",
      label: "Closure Requested By / Maker",
      subLabel: getMakerPositionLabel(permit),
      badge: "CLOSURE BY",
      signatureObj: findSignature(
        signatures,
        (sig) =>
          sig.signature_box_key === "closure_maker_signature" ||
          sameText(sig.workflow_action || sig.action, "request_closure")
      ),
    },
    {
      key: "closure_contractor_ehs_signature",
      label: "Contractor EHS",
      subLabel: "Contractor EHS",
      badge: "Approved By",
      signatureObj: findSignature(
        signatures,
        (sig) =>
          (sig.signature_box_key === "closure_checker_signature" ||
            sig.signature_box_key === "closure_contractor_ehs_signature") &&
          sameText(getSignatureGroupCode(sig, null), "CONTRACTOR_EHS") &&
          isClosureApprove(sig)
      ) || findSignature(
        signatures,
        (sig) =>
          sameText(getSignatureGroupCode(sig, null), "CONTRACTOR_EHS") &&
          isClosureApprove(sig)
      ),
    },
    {
      key: "closure_contractor_engineer_signature",
      label: "Contractor Engineer",
      subLabel: "Contractor Engineer",
      badge: "Approved By",
      signatureObj: findSignature(
        signatures,
        (sig) =>
          (sig.signature_box_key === "closure_checker_signature" ||
            sig.signature_box_key === "closure_contractor_engineer_signature") &&
          sameText(getSignatureGroupCode(sig, null), "CONTRACTOR_ENGINEER") &&
          isClosureApprove(sig)
      ) || findSignature(
        signatures,
        (sig) =>
          sameText(getSignatureGroupCode(sig, null), "CONTRACTOR_ENGINEER") &&
          isClosureApprove(sig)
      ),
    },
    {
      key: "closure_adl_engineer_signature",
      label: "ADL Engineer",
      subLabel: "ADL Engineer",
      badge: "Approved By",
      signatureObj: findSignature(
        signatures,
        (sig) =>
          (sig.signature_box_key === "closure_checker_signature" ||
            sig.signature_box_key === "closure_adl_engineer_signature") &&
          sameText(getSignatureGroupCode(sig, null), "ADL_ENGINEER") &&
          isClosureApprove(sig)
      ) || findSignature(
        signatures,
        (sig) =>
          sameText(getSignatureGroupCode(sig, null), "ADL_ENGINEER") &&
          isClosureApprove(sig)
      ),
    },
    {
      key: "closure_adl_ehs_signature",
      label: "ADL EHS",
      subLabel: "ADL EHS",
      badge: "Approved By",
      signatureObj: findSignature(
        signatures,
        (sig) =>
          (sig.signature_box_key === "closure_checker_signature" ||
            sig.signature_box_key === "closure_adl_ehs_signature") &&
          sameText(getSignatureGroupCode(sig, null), "ADL_EHS") &&
          isClosureApprove(sig)
      ) || findSignature(
        signatures,
        (sig) =>
          sameText(getSignatureGroupCode(sig, null), "ADL_EHS") &&
          isClosureApprove(sig)
      ),
    },
  ];

  return rows
    .filter((row) => row.signatureObj && getSigUrl(row.signatureObj))
    .map((row) => {
      const sig = row.signatureObj;
      let name =
        sig.performed_by_name ||
        sig.actor_name ||
        sig.actor_group_name ||
        "";

      if (
        (!name || String(name).trim().toLowerCase().startsWith("user ")) &&
        permit?.created_by_name &&
        row.key.includes("maker")
      ) {
        name = permit.created_by_name;
      }

      const designation =
        sig.actor_designation ||
        sig.designation ||
        sig.actor_designation_name ||
        sig.actor_role_name ||
        (row.key.includes("maker") ? permit?.created_by_designation : "") ||
        row.subLabel;

      if (!name) {
        name = row.label;
      }

      return {
        key: row.key,
        label: row.label,
        subLabel: row.subLabel,
        designation: designation,
        badge: row.badge,
        name: name,
        signature: getSigUrl(sig),
        signedAtDisplay: sig.signed_at_display || "",
        signedAt: sig.performed_at || sig.created_at || "",
        action: sig.workflow_action || sig.action || "",
        groupName: sig.actor_group_name || "",
        rawSig: sig,
      };
    });
};
export const getClosureRemarksList = (permit) => {
  if (!permit) return [];
  const logs = Array.isArray(permit?.workflow_logs) ? permit.workflow_logs : [];
  const results = [];
  const seenKeys = new Set();

  const cleanUserRemark = (text) => {
    if (!text) return "";
    const str = String(text).trim();
    if (str.startsWith("[SYSTEM]")) return str;
    const idx = str.indexOf("[SYSTEM]");
    if (idx > 0) {
      const userPortion = str.substring(0, idx).trim();
      if (userPortion) return userPortion;
    }
    return str;
  };

  logs.forEach((log) => {
    const action = String(log?.action || "").toLowerCase().trim();
    const statusFrom = String(log?.status_from || "").toLowerCase().trim();
    const statusTo = String(log?.status_to || "").toLowerCase().trim();

    const isClosureRequest =
      action === "request_closure" ||
      action === "closure_request" ||
      (statusTo === "closure_in_progress" && action !== "reject" && action !== "cancel");

    const isClosureReview =
      statusFrom === "closure_in_progress" ||
      (statusTo === "closed" && (action === "approve" || action === "close"));

    if (isClosureRequest || isClosureReview) {
      const rawRemarks = String(log?.remarks || "").trim();
      const userRemark = cleanUserRemark(rawRemarks);

      let author =
        log.performed_by_name ||
        log.actor_name ||
        "";
      if (!author || author.toLowerCase().startsWith("user ")) {
        if (isClosureRequest && permit.created_by_name) {
          author = permit.created_by_name;
        } else if (log.actor_group_name || log.from_group_name) {
          author = log.actor_group_name || log.from_group_name;
        } else {
          author = isClosureRequest ? "Maker" : "Checker";
        }
      }

      let roleLabel = "";
      let badgeColor = "";

      if (isClosureRequest) {
        const isResubmit = statusFrom === "rejected" || action === "submit";
        roleLabel = isResubmit
          ? "Closure Resubmission / Maker"
          : "Closure Requested By / Maker";
        badgeColor = "bg-blue-100 text-blue-800 border-blue-200";
      } else if (action === "reject") {
        const group = log.actor_group_name || log.from_group_name || "Checker";
        roleLabel = `${group} (Closure Rejected)`;
        badgeColor = "bg-red-100 text-red-800 border-red-200";
      } else if (action === "approve" || statusTo === "closed") {
        const group = log.actor_group_name || log.from_group_name || "Checker";
        roleLabel = `${group} (Closure Approved)`;
        badgeColor = "bg-emerald-100 text-emerald-800 border-emerald-200";
      } else {
        const group = log.actor_group_name || log.from_group_name || "Checker";
        roleLabel = `${group} (Closure Review)`;
        badgeColor = "bg-purple-100 text-purple-800 border-purple-200";
      }

      const displayRemark = userRemark || rawRemarks;
      if (displayRemark || isClosureRequest || action === "approve" || action === "reject") {
        const defaultText = isClosureRequest
          ? (statusFrom === "rejected" || action === "submit" ? "Closure resubmitted" : "Closure requested")
          : "Approved";
        const finalRemark = displayRemark || defaultText;
        const key = `${log.id || ""}_${action}_${finalRemark}`;
        if (!seenKeys.has(key)) {
          seenKeys.add(key);
          results.push({
            id: log.id,
            action,
            isClosureRequest,
            author,
            roleLabel,
            badgeColor,
            date:
              log.performed_at || log.created_at
                ? formatSignatureDateTime(log.performed_at || log.created_at)
                : "",
            remark: finalRemark,
            attachments: Array.isArray(log.attachments) ? log.attachments : [],
          });
        }
      }
    }
  });

  // Also check top-level closure_remarks if not yet added
  const topClosureRemarks = permit.closure_remarks || permit.form_data?.closure_remarks;
  if (topClosureRemarks && !results.some((r) => r.remark.includes(String(topClosureRemarks).trim()))) {
    results.unshift({
      id: "permit_closure_remarks",
      action: "request_closure",
      isClosureRequest: true,
      author: permit.created_by_name || "Maker",
      roleLabel: "Closure Requested By / Maker",
      badgeColor: "bg-blue-100 text-blue-800 border-blue-200",
      date:
        permit.workflow_moved_at || permit.updated_at
          ? formatSignatureDateTime(permit.workflow_moved_at || permit.updated_at)
          : "",
      remark: String(topClosureRemarks).trim(),
      attachments: [],
    });
  }

  return results;
};

export const getCancellationRemarksList = (permit) => {
  if (!permit) return [];
  const logs = Array.isArray(permit?.workflow_logs) ? permit.workflow_logs : [];
  const signatures = getCompletedSignatures(permit);
  const results = [];
  const seenKeys = new Set();

  logs.forEach((log) => {
    const action = String(log?.action || "").toLowerCase().trim();
    const statusTo = String(log?.status_to || "").toLowerCase().trim();

    const isCancel = action === "cancel" || statusTo === "cancelled";

    if (isCancel) {
      const rawRemarks = String(log?.remarks || log?.reason || log?.comment || "").trim();
      let author = log.performed_by_name || log.actor_name || "";
      if (!author || author.toLowerCase().startsWith("user ")) {
        if (log.actor_group_name || log.from_group_name) {
          author = log.actor_group_name || log.from_group_name;
        } else {
          author = "Checker";
        }
      }

      const group = log.actor_group_name || log.from_group_name || "Checker";
      const roleLabel = `${group} (Cancelled)`;
      const badgeColor = "bg-rose-100 text-rose-800 border-rose-200";

      // Look for any signature associated with this cancellation
      const cancelSig = findSignature(
        signatures,
        (sig) =>
          sameText(sig.workflow_action, "cancel") ||
          sameText(sig.signature_box_key, "cancel_signature") ||
          (log.id && sig.workflow_log_id === log.id)
      );

      const displayRemark = rawRemarks || "Permit cancelled";
      const key = `${log.id || ""}_${action}_${displayRemark}`;
      if (!seenKeys.has(key)) {
        seenKeys.add(key);
        results.push({
          id: log.id || `cancel_${results.length}`,
          action: "cancel",
          author,
          roleLabel,
          badgeColor,
          date:
            log.performed_at || log.created_at
              ? formatSignatureDateTime(log.performed_at || log.created_at)
              : "",
          remark: displayRemark,
          attachments: Array.isArray(log.attachments) ? log.attachments : [],
          signature: cancelSig ? getSigUrl(cancelSig) : null,
          rawSig: cancelSig || null,
        });
      }
    }
  });

  // Also check top-level latest_cancel_reason if not yet added
  const topCancelReason = permit.latest_cancel_reason || permit.form_data?.cancellation_reason || permit.cancellation_reason;
  if (topCancelReason && !results.some((r) => r.remark.includes(String(topCancelReason).trim()))) {
    const cancelSig = findSignature(
      signatures,
      (sig) => sameText(sig.workflow_action, "cancel")
    );
    results.unshift({
      id: "permit_top_cancel_reason",
      action: "cancel",
      author: permit.latest_cancelled_by_name || "Checker",
      roleLabel: "Checker (Cancelled)",
      badgeColor: "bg-rose-100 text-rose-800 border-rose-200",
      date: permit.latest_cancelled_at_display || (permit.updated_at ? formatSignatureDateTime(permit.updated_at) : ""),
      remark: String(topCancelReason).trim(),
      attachments: [],
      signature: cancelSig ? getSigUrl(cancelSig) : null,
      rawSig: cancelSig || null,
    });
  }

  return results;
};

export default function PTWSignOffSection({ permit, title = "Sign Off" }) {
  const [signatureUser, setSignatureUser] = useState(null);

  const issueRows = useMemo(() => buildIssueSignatureRows(permit), [permit]);
  const closureRows = useMemo(() => buildClosureSignatureRows(permit), [permit]);
  const closureRemarks = useMemo(() => getClosureRemarksList(permit), [permit]);
  const cancellationRemarks = useMemo(() => getCancellationRemarksList(permit), [permit]);

  const hasIssueUsers = issueRows.length > 0;
  const hasClosureUsers = closureRows.length > 0;
  const hasClosureRemarks = closureRemarks.length > 0;
  const hasCancellationRemarks = cancellationRemarks.length > 0;

  if (!hasIssueUsers && !hasClosureUsers && !hasClosureRemarks && !hasCancellationRemarks) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-1">
          <h3 className="text-sm font-bold text-slate-900">{title}</h3>
        </div>
        <p className="text-xs text-slate-500 italic">No signatures captured yet.</p>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-4">
          <h3 className="text-sm font-bold text-slate-900">{title}</h3>
        </div>

        {hasIssueUsers && (
          <div>
            <div className="mb-2 flex items-center justify-between">
              <h4 className="text-xs font-bold uppercase tracking-wide text-slate-600">
                Issue Signatures
              </h4>
            </div>

            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              {issueRows.map((user) => (
                <PermitSignatureCard
                  key={user.key}
                  user={user}
                  onView={setSignatureUser}
                />
              ))}
            </div>
          </div>
        )}

        {hasClosureUsers && (
          <div className={`${hasIssueUsers ? "mt-5 border-t border-slate-200 pt-4" : ""}`}>
            <div className="mb-2 flex items-center justify-between">
              <h4 className="text-xs font-bold uppercase tracking-wide text-slate-600">
                Closure Signatures
              </h4>
            </div>

            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              {closureRows.map((user) => (
                <PermitSignatureCard
                  key={user.key}
                  user={user}
                  onView={setSignatureUser}
                />
              ))}
            </div>
          </div>
        )}

        {/* {hasClosureRemarks && (
          <div className={`${hasIssueUsers || hasClosureUsers ? "mt-5 border-t border-slate-200 pt-4" : ""}`}>
            <div className="mb-2 flex items-center justify-between">
              <h4 className="text-xs font-bold uppercase tracking-wide text-slate-600">
                Closure Overall Remarks
              </h4>
            </div>

            <div className="space-y-3">
              {closureRemarks.map((item, idx) => (
                <div
                  key={item.id || idx}
                  className="rounded-xl border border-slate-200 bg-slate-50/70 p-3.5 shadow-2xs"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-slate-900">
                        {item.author}
                      </span>
                      <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold border ${item.badgeColor}`}>
                        {item.roleLabel}
                      </span>
                    </div>
                    {item.date && (
                      <span className="text-[11px] text-slate-400 font-medium">
                        {item.date}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-800 whitespace-pre-wrap leading-relaxed font-medium">
                    {item.remark}
                  </p>
                  {item.attachments && item.attachments.length > 0 && (
                    <div className="mt-2.5 flex flex-wrap gap-2">
                      {item.attachments.map((att, attIdx) => {
                        const url = resolveMediaUrl(att.file_url || att.file);
                        return (
                          <a
                            key={attIdx}
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-100"
                          >
                            <Paperclip className="h-3.5 w-3.5 text-slate-500" />
                            Attachment {attIdx + 1}
                          </a>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )} */}

        {hasCancellationRemarks && (
          <div className={`${hasIssueUsers || hasClosureUsers || hasClosureRemarks ? "mt-5 border-t border-slate-200 pt-4" : ""}`}>
            <div className="mb-2 flex items-center justify-between">
              <h4 className="text-xs font-bold uppercase tracking-wide text-rose-600">
                Cancellation Remarks & History
              </h4>
            </div>

            <div className="space-y-3">
              {cancellationRemarks.map((item, idx) => (
                <div
                  key={item.id || idx}
                  className="rounded-xl border border-rose-200 bg-rose-50/50 p-3.5 shadow-2xs"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-slate-900">
                        {item.author}
                      </span>
                      <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold border ${item.badgeColor}`}>
                        {item.roleLabel}
                      </span>
                    </div>
                    {item.date && (
                      <span className="text-[11px] text-slate-500 font-medium">
                        {item.date}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-rose-950 whitespace-pre-wrap leading-relaxed font-medium">
                    {item.remark}
                  </p>
                  {item.signature && (
                    <div className="mt-2.5 flex items-center">
                      <button
                        type="button"
                        onClick={() =>
                          setSignatureUser({
                            name: item.author,
                            signature: item.signature,
                            label: item.roleLabel,
                            signedAtDisplay: item.date,
                            designation: "Checker",
                          })
                        }
                        className="inline-flex items-center gap-1.5 rounded-lg border border-rose-300 bg-white px-2.5 py-1 text-[11px] font-semibold text-rose-700 hover:bg-rose-50 cursor-pointer shadow-2xs transition-colors"
                      >
                        View Signature
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <SignatureViewModal
        open={!!signatureUser}
        user={signatureUser}
        onClose={() => setSignatureUser(null)}
      />
    </>
  );
}
